import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { portalReportsAPI } from '../../services/portalAPI';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import PortalFooter from '../../components/Layout/PortalFooter';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import '../../styles/portal-brand.css';

interface ReorderSuggestion {
  formula_id: number;
  formula_name: string;
  animal_type: string;
  is_pelleted: boolean;
  package_type: string;
  avg_quantity_lbs: number;
  last_quantity_lbs: number;
  last_order_date: string;
  days_since_last: number;
  avg_interval_days: number;
  order_count: number;
  urgency: 'high' | 'medium' | 'normal';
  reason: string;
}

interface SuggestionsData {
  suggestions: ReorderSuggestion[];
  total_suggestions: number;
}

const PortalReorderSuggestions: React.FC = () => {
  const { user } = usePortalAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<SuggestionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadSuggestions();
  }, []);

  const loadSuggestions = async () => {
    try {
      setLoading(true);
      const response = await portalReportsAPI.getReorderSuggestions();
      setData(response.data);
    } catch (err: any) {
      setError('Failed to load reorder suggestions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (formulaId: number) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(formulaId)) {
      newSelected.delete(formulaId);
    } else {
      newSelected.add(formulaId);
    }
    setSelectedItems(newSelected);
  };

  const selectAll = () => {
    if (!data) return;
    const allIds = new Set(data.suggestions.map(s => s.formula_id));
    setSelectedItems(allIds);
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleAddToOrder = () => {
    if (!user?.permissions.can_place_orders) {
      alert('You do not have permission to place orders');
      return;
    }

    if (!data) return;

    const selectedSuggestions = data.suggestions.filter(s => selectedItems.has(s.formula_id));

    const reorderData = {
      items: selectedSuggestions.map(s => ({
        formula: s.formula_id,
        formula_name: s.formula_name,
        is_pellet: s.is_pelleted,
        package_type: s.package_type,
        suggested_quantity: s.avg_quantity_lbs,
      })),
    };

    localStorage.setItem('reorder_data', JSON.stringify(reorderData));
    navigate('/portal/place-order');
  };

  const getUrgencyStyle = (urgency: string): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      padding: '0.35rem 0.75rem',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '600',
      textTransform: 'uppercase',
    };

    switch (urgency) {
      case 'high':
        return { ...baseStyle, backgroundColor: '#fee2e2', color: '#991b1b' };
      case 'medium':
        return { ...baseStyle, backgroundColor: '#fef3c7', color: '#92400e' };
      default:
        return { ...baseStyle, backgroundColor: '#d1fae5', color: '#065f46' };
    }
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>Analyzing your ordering patterns...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorState}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <TutorialAutoStart pageKey="reorder-suggestions" />
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>Reorder Suggestions</h1>
          <p style={styles.subtitle}>
            Based on your ordering history, here are products you may need to reorder soon
          </p>
        </div>
        <TutorialHelpButton pageKey="reorder-suggestions" />
      </div>

      {/* Selection Actions Bar */}
      {data && data.suggestions.length > 0 && user?.permissions.can_place_orders && (
        <div style={styles.actionsBar}>
          <div style={styles.selectionInfo}>
            {selectedItems.size} of {data.suggestions.length} items selected
          </div>
          <div style={styles.actionButtons}>
            <button onClick={selectAll} style={styles.linkButton}>
              Select All
            </button>
            <button onClick={clearSelection} style={styles.linkButton}>
              Clear Selection
            </button>
            {selectedItems.size > 0 && (
              <button onClick={handleAddToOrder} style={styles.primaryButton} data-tutorial="add-to-order">
                Add {selectedItems.size} Item{selectedItems.size > 1 ? 's' : ''} to Order
              </button>
            )}
          </div>
        </div>
      )}

      {/* Suggestions List */}
      {data && data.suggestions.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>?</div>
          <h3 style={styles.emptyTitle}>No reorder suggestions yet</h3>
          <p style={styles.emptyText}>
            We need more order history to make personalized suggestions.
            Keep ordering and we'll start tracking your patterns!
          </p>
        </div>
      ) : (
        <div style={styles.suggestionsList} data-tutorial="suggestions-list">
          {data?.suggestions.map((suggestion) => (
            <div
              key={suggestion.formula_id}
              style={{
                ...styles.suggestionCard,
                ...(selectedItems.has(suggestion.formula_id) ? styles.selectedCard : {}),
              }}
              onClick={() => user?.permissions.can_place_orders && toggleSelection(suggestion.formula_id)}
            >
              {user?.permissions.can_place_orders && (
                <div style={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={selectedItems.has(suggestion.formula_id)}
                    onChange={() => toggleSelection(suggestion.formula_id)}
                    style={styles.checkboxInput}
                  />
                </div>
              )}

              <div style={styles.suggestionContent}>
                <div style={styles.suggestionHeader}>
                  <div style={styles.suggestionTitle}>
                    <span style={styles.formulaName}>{suggestion.formula_name}</span>
                    <span style={getUrgencyStyle(suggestion.urgency)}>
                      {suggestion.urgency === 'high' ? 'Overdue' : suggestion.urgency === 'medium' ? 'Due Soon' : 'Coming Up'}
                    </span>
                  </div>
                  <div style={styles.suggestionMeta}>
                    <span style={styles.animalType}>{suggestion.animal_type}</span>
                    <span style={styles.metaDot}>•</span>
                    <span>{suggestion.is_pelleted ? 'Pellet' : 'Mash'}</span>
                    <span style={styles.metaDot}>•</span>
                    <span>{suggestion.order_count} previous orders</span>
                  </div>
                </div>

                <div style={styles.suggestionDetails}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Last Order</span>
                    <span style={styles.detailValue}>
                      {new Date(suggestion.last_order_date).toLocaleDateString()}
                      <span style={styles.daysAgo}>({suggestion.days_since_last} days ago)</span>
                    </span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Typical Interval</span>
                    <span style={styles.detailValue}>Every {suggestion.avg_interval_days} days</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Suggested Quantity</span>
                    <span style={styles.detailValue}>{formatNumber(suggestion.avg_quantity_lbs)} lbs</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Last Quantity</span>
                    <span style={styles.detailValue}>{formatNumber(suggestion.last_quantity_lbs)} lbs</span>
                  </div>
                </div>

                <div style={styles.reasonBox}>
                  <span style={styles.reasonIcon}>i</span>
                  <span style={styles.reasonText}>{suggestion.reason}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PortalFooter />
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '2rem 1.5rem',
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '2rem',
  },
  headerLeft: {},
  title: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#3F2021',
    margin: '0 0 0.5rem 0',
  },
  subtitle: {
    fontSize: '1rem',
    color: '#508590',
    margin: 0,
  },
  loadingState: {
    textAlign: 'center',
    padding: '3rem',
    color: '#508590',
    fontSize: '1.125rem',
  },
  errorState: {
    backgroundColor: '#fee2e2',
    border: '1px solid #fca5a5',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '4px',
    textAlign: 'center',
  },
  actionsBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    border: '2px solid #C3AD8B',
    borderRadius: '8px',
    padding: '1rem 1.5rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  selectionInfo: {
    fontSize: '0.95rem',
    color: '#3F2021',
    fontWeight: '500',
  },
  actionButtons: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#508590',
    cursor: 'pointer',
    fontSize: '0.9rem',
    textDecoration: 'underline',
  },
  primaryButton: {
    backgroundColor: '#E73C3E',
    color: '#FFFFFF',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.95rem',
  },
  emptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    padding: '4rem 2rem',
    textAlign: 'center',
  },
  emptyIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1.5rem',
    fontSize: '2rem',
    color: '#508590',
  },
  emptyTitle: {
    fontSize: '1.25rem',
    color: '#3F2021',
    marginBottom: '0.5rem',
  },
  emptyText: {
    fontSize: '1rem',
    color: '#508590',
    maxWidth: '400px',
    margin: '0 auto',
    lineHeight: '1.6',
  },
  suggestionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  suggestionCard: {
    display: 'flex',
    backgroundColor: '#FFFFFF',
    borderRadius: '8px',
    border: '2px solid #C3AD8B',
    padding: '1.5rem',
    cursor: 'pointer',
    transition: 'all 0.2s',
    gap: '1rem',
  },
  selectedCard: {
    borderColor: '#E73C3E',
    backgroundColor: '#fef2f2',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '0.25rem',
  },
  checkboxInput: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  suggestionContent: {
    flex: 1,
  },
  suggestionHeader: {
    marginBottom: '1rem',
  },
  suggestionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '0.5rem',
    flexWrap: 'wrap',
  },
  formulaName: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#3F2021',
  },
  suggestionMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
    color: '#508590',
    flexWrap: 'wrap',
  },
  animalType: {
    fontWeight: '500',
  },
  metaDot: {
    color: '#C3AD8B',
  },
  suggestionDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  detailLabel: {
    fontSize: '0.75rem',
    color: '#508590',
    textTransform: 'uppercase',
    fontWeight: '600',
  },
  detailValue: {
    fontSize: '0.95rem',
    color: '#3F2021',
    fontWeight: '500',
  },
  daysAgo: {
    color: '#508590',
    fontWeight: 'normal',
    marginLeft: '0.5rem',
    fontSize: '0.875rem',
  },
  reasonBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '1rem',
    backgroundColor: '#FAF9F6',
    borderRadius: '4px',
    border: '1px solid #C3AD8B',
  },
  reasonIcon: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#508590',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    flexShrink: 0,
  },
  reasonText: {
    fontSize: '0.9rem',
    color: '#3F2021',
    lineHeight: '1.5',
  },
};

export default PortalReorderSuggestions;
